import axios from 'axios';
import { axiosPrivate } from '../api/axios';
import { useEffect } from 'react';
import useRefreshToken from './useRefreshToken';
import useAuth from './useAuth';

const useAxiosPrivate = () => {
    const refresh = useRefreshToken();
    const { auth } = useAuth();

    useEffect(() => {
        const attachAccessToken = (config) => {
            const accessToken = auth?.user?.accessToken;
            if (!config.headers['Authorization'] && accessToken) {
                config.headers['Authorization'] = `Bearer ${accessToken}`;
            }
            return config;
        };

        const retryWithRefresh = async (error, client) => {
            const prevRequest = error?.config;
            const status = error?.response?.status;
            const isAuthRoute = prevRequest?.url?.includes('/auth/');
            if ((status === 403 || status === 401) && !prevRequest?.sent && !isAuthRoute) {
                prevRequest.sent = true;
                const newAccessToken = await refresh();
                prevRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                return client(prevRequest);
            }
            return Promise.reject(error);
        };

        const requestIntercept = axios.interceptors.request.use(
            attachAccessToken,
            (error) => Promise.reject(error)
        );
        const privateRequestIntercept = axiosPrivate.interceptors.request.use(
            attachAccessToken,
            (error) => Promise.reject(error)
        );

        const responseIntercept = axios.interceptors.response.use(
            (response) => response,
            (error) => retryWithRefresh(error, axios)
        );
        const privateResponseIntercept = axiosPrivate.interceptors.response.use(
            (response) => response,
            (error) => retryWithRefresh(error, axiosPrivate)
        );

        return () => {
            axios.interceptors.request.eject(requestIntercept);
            axios.interceptors.response.eject(responseIntercept);
            axiosPrivate.interceptors.request.eject(privateRequestIntercept);
            axiosPrivate.interceptors.response.eject(privateResponseIntercept);
        };
    }, [auth, refresh]);

    return axiosPrivate;
};

export default useAxiosPrivate;
